import { Duration, Instant } from "@js-joda/core";
import React, { ReactElement, useCallback, useState } from "react";
import { VisitBroadcastId } from "../../messages/ObjectIds";
import { Visit } from "../../system/AppState/AppVisitsState";
import { List } from "../../utils/List";
import { undefinedToNull } from "../../utils/undefinedToNull";
import { isDefined } from "../Generic/IsDefined";
import { VisitPendingConfirmationBanner } from "./VisitPendingConfirmationBanner";

interface Props {
  visits: List<Visit.Available>;
  onConfirmAssignmentClick: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
}

export function VisitPendingConfirmationBannerList(props: Props): ReactElement {
  const [timeFilter, setTimeFilter] = useState(Instant.now());

  const onTimerEnd = useCallback(() => setTimeFilter(Instant.now()), [setTimeFilter]);

  const pendingConfirmationVisits = getPendingConfirmationVisits(props.visits, timeFilter).sortBy(
    (visit) => visit.publicVisitBroadcastDetails.startTimeLocal
  );

  if (pendingConfirmationVisits.length() === 0) {
    return <></>;
  }

  const onShowDetailsClick = (visitId: VisitBroadcastId, isReviewOnly: boolean) => {
    const visit = pendingConfirmationVisits.filter(({ id }) => id === visitId).first();
    if (isDefined(visit)) {
      props.onConfirmAssignmentClick(visit.id, isReviewOnly);
    }
  };

  const currVisit = pendingConfirmationVisits.first();

  return (
    <>
      {currVisit !== undefined ? (
        isDefined(currVisit.caregiverVisitEngagementDetails.pendingConfirmationInstances) &&
        isDefined(currVisit.caregiverVisitEngagementDetails.pendingConfirmationUntil) ? (
          <VisitPendingConfirmationBanner
            key={`${currVisit.id}`}
            visitId={currVisit.id}
            pendingConfirmationUntil={
              currVisit.caregiverVisitEngagementDetails.pendingConfirmationUntil
            }
            pendingConfirmationConfirmedAt={undefinedToNull(
              currVisit.caregiverVisitEngagementDetails.pendingConfirmationConfirmedAt
            )}
            onShowDetailsClick={onShowDetailsClick}
            onTimerEnd={onTimerEnd}
          />
        ) : null
      ) : null}
    </>
  );
}

const getPendingConfirmationVisits = (visits: List<Visit.Available>, now: Instant) => {
  return visits.filter((visit) => {
    const { pendingConfirmationInstances, pendingConfirmationShifts, pendingConfirmationUntil } =
      visit.caregiverVisitEngagementDetails;

    const hasPendingConfirmationInstances =
      isDefined(pendingConfirmationInstances) && pendingConfirmationInstances.length > 0;

    const hasPendingConfirmationShifts =
      isDefined(pendingConfirmationShifts) && pendingConfirmationShifts.length > 0;

    const hasPendingConfirmationUntil =
      isDefined(pendingConfirmationUntil) &&
      !Duration.between(now, pendingConfirmationUntil).isNegative();

    return (
      (hasPendingConfirmationInstances || hasPendingConfirmationShifts) &&
      hasPendingConfirmationUntil
    );
  });
};
